import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';

interface ConvertPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional override copy for context (e.g. "to download this document"). */
  reason?: string;
  /** Optional override for the modal title. */
  title?: string;
}

/**
 * Conversion dialog shown to anonymous visitors when they try to download,
 * copy or export a generated document. Routes them to beta access application
 * (or sign-in for invite holders).
 */
export const ConvertPrompt = ({ open, onOpenChange, reason, title }: ConvertPromptProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search}${location.hash}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-2xl">{title ?? 'Save & export your work'}</DialogTitle>
          <DialogDescription className="text-center">
            {reason
              ? reason
              : 'Sign in to keep this document in your account, or apply for beta access to create a new account.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              navigate(`/auth?redirect=${encodeURIComponent(returnPath)}`);
            }}
          >
            Sign In
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              navigate(`/beta_access?redirect=${encodeURIComponent(returnPath)}`);
            }}
          >
            Apply for Beta Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
